/**
 * `useDocument` that keeps waiting.
 *
 * The stock hook awaits `repo.find` ONCE. `find` rejects the moment every
 * source has said "not here", and on a reload that is routinely the first
 * thing that happens: local storage has nothing and the peer that does has not
 * connected yet. The hook swallows the rejection and never asks again, so the
 * query goes on to become ready a second later and the component never hears
 * of it. A document pane read "Loading…" until the page was refreshed into a
 * luckier race.
 *
 * This one follows the query itself. `unavailable` is a state the query leaves
 * when a peer arrives, so it is treated as still loading, and the document
 * shows up when the query turns ready, however late. Same return shape as the
 * stock hook, so it is a drop-in.
 */

import type { ChangeOptions } from '@automerge/automerge'
import type {
  AnyDocumentId,
  ChangeFn,
  Doc,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import { useDocumentReadyNonce } from './document-ready'
import { holdDocument } from './held-docs'
import { useOptionalTabRepoBoot } from './TabRepoProvider'

const noop = () => {}
const nothing = () => noop

/**
 * Hold what this component reads for as long as it is mounted (RFC 0317 §3d).
 * The last release evicts the document from the tab repo; on any other repo
 * this does nothing.
 */
function useHold(
  repo: Repo,
  ids: readonly (AnyDocumentId | null | undefined)[],
) {
  const key = [...new Set(ids.filter(Boolean).map(String))]
    .sort()
    .join('\u0000')
  useEffect(() => {
    if (!key) return
    const releases = key.split('\u0000').map((id) => holdDocument(repo, id))
    return () => {
      for (const release of releases) release()
    }
  }, [repo, key])
}

/**
 * The list form of `useDocument`.
 *
 * The upstream bulk hook has the same one-shot `repo.find` failure mode as
 * `useDocument` did. A project catalog is a list because a window can show
 * panes from several projects, so it follows every query until a late peer
 * makes it ready.
 */
export function useDocuments<T>(
  ids: readonly AnyDocumentId[],
): Map<string, Doc<T>> {
  const repo = useRepo() as unknown as Repo
  const boot = useOptionalTabRepoBoot()
  const key = ids.join('\u0000')
  // A caller often creates its ids array while rendering. Its contents, not
  // that allocation, decide whether these subscriptions are replaced.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by contents
  const stableIds = useMemo(() => ids, [key])
  useEffect(() => {
    for (const id of stableIds) boot?.sharing?.requestDocument(String(id))
  }, [boot, stableIds])
  useHold(repo, stableIds)
  const progress = useMemo(
    () => stableIds.map((id) => repo.findWithProgress<T>(id)),
    [repo, stableIds],
  )
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const off = progress.map((query) => query.subscribe(cb))
      return () => off.forEach((stop) => stop())
    },
    [progress],
  )
  // A query's snapshot is stable until it changes. Keeping this array stable
  // is required by useSyncExternalStore.
  const snapshot = useMemo(() => {
    let last = progress.map((query) => query.peek())
    return () => {
      const next = progress.map((query) => query.peek())
      if (next.every((state, i) => state === last[i])) return last
      last = next
      return last
    }
  }, [progress])
  const states = useSyncExternalStore(subscribe, snapshot)
  const handles = useMemo(
    () =>
      states.map((state) =>
        state.state === 'ready' ? (state.handle as DocHandle<T>) : undefined,
      ),
    [states],
  )
  // The documents the ids resolved to, when a move took one elsewhere.
  const asked = new Set(stableIds.map(String))
  useHold(
    repo,
    handles.map((h) =>
      h && !asked.has(String(h.documentId)) ? h.documentId : null,
    ),
  )
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    const changed = () => setRevision((n) => n + 1)
    for (const handle of handles) handle?.on('change', changed)
    return () => {
      for (const handle of handles) handle?.off('change', changed)
    }
  }, [handles])

  // The map is memoised because its identity is what every caller keys off:
  // `useProjectCatalogs` rebuilds a catalog per project from it, and the shell
  // reads that while laying out panes. Built fresh each render, an unrelated
  // render of the parent would redo all of that, and a change arriving during
  // sync would do it again per event. `revision` is the dependency that says a
  // document really changed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: revision is the change signal
  return useMemo(() => {
    const docs = new Map<string, Doc<T>>()
    for (const [i, handle] of handles.entries()) {
      if (handle) docs.set(String(stableIds[i]), handle.doc())
    }
    return docs
  }, [handles, stableIds, revision])
}

export function useDocument<T>(
  id: AnyDocumentId | undefined | null,
): [Doc<T> | undefined, (fn: ChangeFn<T>, options?: ChangeOptions<T>) => void] {
  const repo = useRepo() as unknown as Repo
  const boot = useOptionalTabRepoBoot()
  useEffect(() => {
    if (id) boot?.sharing?.requestDocument(String(id))
  }, [boot, id])
  // A query that gave up before this tab was let in is made again once the
  // shared worker holds the document (document-ready.ts).
  const readyNonce = useDocumentReadyNonce(id ? String(id) : undefined)
  // biome-ignore lint/correctness/useExhaustiveDependencies: readyNonce re-asks
  const progress = useMemo(
    () => (id ? repo.findWithProgress<T>(id) : undefined),
    [repo, id, readyNonce],
  )
  const subscribe = useMemo(
    () => (progress ? (cb: () => void) => progress.subscribe(cb) : nothing),
    [progress],
  )
  const state = useSyncExternalStore(subscribe, () => progress?.peek())
  const handle: DocHandle<T> | undefined =
    state?.state === 'ready' ? state.handle : undefined
  // Hold the id asked for AND the document it resolved to: a moved document
  // (a session page that closed, RFC 0360) is a different id, and a page
  // nobody holds is evicted from under the view, which then shows a handle
  // the repo no longer feeds until the pane is reloaded.
  const resolved = handle?.documentId
  useHold(repo, [
    id,
    resolved && String(resolved) !== String(id) ? resolved : null,
  ])

  // The snapshot is paired with the handle it came from, so the render after an
  // id change never hands over the previous document under the new id.
  const [snap, setSnap] = useState(() => ({ handle, doc: handle?.doc() }))
  useEffect(() => {
    if (!handle) return
    const read = () => setSnap({ handle, doc: handle.doc() })
    read()
    handle.on('change', read)
    return () => {
      handle.off('change', read)
    }
  }, [handle])

  const change = useCallback(
    (fn: ChangeFn<T>, options?: ChangeOptions<T>) =>
      handle?.change(fn, options),
    [handle],
  )
  const doc = handle && snap.handle === handle ? snap.doc : handle?.doc()
  return [doc, change]
}
